/**
 * /learn/prizes/:prizeId/claim — prize detail + claim flow.
 *
 * Layout matches Figma `6475:124104`:
 *   • Gradient hero panel — image carousel (left) + product info (right).
 *   • "Description" tab strip + Specifications table.
 *   • Bullet sections — Product details / Benefits / Delivery — only
 *     rendered when the backend supplies content for them.
 *   • Sticky bottom action bar with "Go back" + "Claim now".
 *   • Floating squirrel mascot bottom-right.
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon, StarIcon } from '@heroicons/react/24/solid';
import { slideUp, EASE_OUT } from '@/lib/motion';
import { useImageUrls } from '../../services/uploads';
import { usePrize } from '../../hooks/learner/usePrizes';
import PrizeClaimFlow from '../../components/learner/PrizeClaimFlow';
import { getPoints } from '../../api/me';
import { useTheme } from '@/contexts/ThemeContext';
import type { LearnerPrizeDetail } from '../../types/learnerPrizes';

const PRIZE_CATEGORY_LABEL: Record<string, string> = {
  merchandise: 'Merchandise',
  gift_card: 'Gift card',
  experience: 'Experience',
  digital: 'Digital',
  certificate: 'Certificate',
  custom: 'Other',
};

const SWATCH_COLOR_MAP: Record<string, string> = {
  black: '#1f1f1f',
  white: '#f5f5f5',
  grey: '#9695a2',
  gray: '#9695a2',
  red: '#dc2626',
  orange: '#ea580c',
  yellow: '#facc15',
  green: '#16a34a',
  blue: '#2563eb',
  navy: '#1e3a8a',
  purple: 'rgb(var(--color-primary))',
  pink: '#ec4899',
  brown: '#92400e',
};

function swatchFor(name: string): string {
  return SWATCH_COLOR_MAP[name.toLowerCase()] ?? '#9695a2';
}

const LearnerPrizeClaimPage: React.FC = () => {
  const { prizeId } = useParams<{ prizeId: string }>();
  const navigate = useNavigate();

  const { data: prize, isLoading, isError } = usePrize(prizeId);
  const { data: points } = useQuery({
    queryKey: ['me', 'gamification', 'points'],
    queryFn: getPoints,
  });
  const [claimOpen, setClaimOpen] = useState(false);

  const balance = points?.balance ?? 0;

  if (isLoading) return <ClaimSkeleton />;

  if (isError || !prize) {
    return (
      <div className="max-w-[640px] mx-auto px-4 py-12 text-center">
        <p className="text-sm text-error-500 mb-4">We couldn't find that prize.</p>
        <Link to="/learn/prizes" className="text-sm font-bold text-primary-500 hover:underline">
          ← Back to Prize Shop
        </Link>
      </div>
    );
  }

  const affordable = balance >= prize.price_points;

  return (
    <motion.div {...slideUp} className="relative space-y-6 pb-28">
      {/* Breadcrumb path — PageHeader style (hero header page) */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 px-2">
        <Link
          to="/learn/dashboard"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Home
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <Link
          to="/learn/prizes"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Points &amp; Prizes
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <span className="body-small text-primary-600 dark:text-primary-350 font-medium truncate max-w-[240px]">
          {prize.title}
        </span>
      </nav>

      <PrizeHero
        prize={prize}
        balance={balance}
        affordable={affordable}
      />
      <PrizeBody prize={prize} />

      <ActionBar
        canClaim
        isPending={false}
        onCancel={() => navigate('/learn/prizes')}
        onConfirm={() => setClaimOpen(true)}
      />

      {/* Confirm / success / insufficient-points overlay (Figma trio). The
          bar stays enabled while short on points — the overlay explains. */}
      <PrizeClaimFlow
        prize={prize}
        balance={balance}
        isOpen={claimOpen}
        onClose={() => setClaimOpen(false)}
      />
    </motion.div>
  );
};

export default LearnerPrizeClaimPage;

// ─── Hero ───────────────────────────────────────────────────────────────────

interface PrizeHeroProps {
  prize: LearnerPrizeDetail;
  balance: number;
  affordable: boolean;
}

function PrizeHero({ prize }: PrizeHeroProps) {
  const reduceMotion = useReducedMotion();
  const { theme } = useTheme();
  const [activeIdx, setActiveIdx] = useState(0);
  const images = useImageUrls(prize.media_urls ?? []);
  const hasImages = images.length > 0;
  const hasMultiple = images.length > 1;

  const next = () => setActiveIdx((i) => (i + 1) % images.length);
  const prev = () => setActiveIdx((i) => (i - 1 + images.length) % images.length);

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE_OUT }}
      className="relative"
    >
      <div className="relative flex justify-end mb-3 px-2">
        <span className="inline-flex items-center gap-1.5 text-sm sm:text-base font-bold text-text-primary">
          Points necessary: {prize.price_points.toLocaleString()}
          <StarIcon className="w-5 h-5 text-warning-500" aria-hidden="true" />
        </span>
      </div>
      <div
        className="relative overflow-hidden rounded-3xl px-6 sm:px-10 py-8"
        style={{
          background:
            theme === 'dark'
              ? 'linear-gradient(135deg, #2a2230 0%, #322840 35%, #2e2745 70%, #251f38 100%)'
              : 'linear-gradient(135deg, #f5e8e1 0%, #f5e0e8 35%, #e8d5f0 70%, #d8c8eb 100%)',
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 lg:gap-12 items-center">
          {/* Carousel */}
          <div className="space-y-4">
            <div className="relative aspect-square w-full max-w-[420px] mx-auto">
              {hasImages ? (
                <img
                  key={activeIdx}
                  src={images[activeIdx] ?? ''}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain"
                />
              ) : (
                <div className="absolute inset-0 rounded-2xl bg-white/40 flex items-center justify-center">
                  <span className="text-sm text-text-tertiary">No image</span>
                </div>
              )}
              {hasMultiple && (
                <>
                  <button
                    type="button"
                    onClick={prev}
                    aria-label="Previous image"
                    className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 rounded-full bg-text-primary/85 text-background-primary hover:bg-text-primary active:scale-[0.95] transition-[background-color,transform] duration-fast ease-out-strong"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={next}
                    aria-label="Next image"
                    className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 rounded-full bg-text-primary/85 text-background-primary hover:bg-text-primary active:scale-[0.95] transition-[background-color,transform] duration-fast ease-out-strong"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                  <span className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-md bg-text-primary/85 text-background-primary text-[11px] font-semibold tabular-nums">
                    {activeIdx + 1}/{images.length}
                  </span>
                </>
              )}
            </div>
            {hasMultiple && (
              <div className="flex items-center justify-center gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    aria-label={`Image ${i + 1}`}
                    className={`relative w-16 h-16 rounded-full overflow-hidden border-2 transition-[border-color] duration-fast ease-out-strong ${
                      i === activeIdx ? 'border-primary-500' : 'border-transparent'
                    }`}
                  >
                    <img
                      src={img ?? ''}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-5">
            <header className="space-y-2">
              <h1 className="text-2xl font-bold text-text-primary leading-tight">
                {prize.title}
              </h1>
              {prize.description && (
                <p className="text-sm text-text-secondary leading-relaxed">
                  {prize.description}
                </p>
              )}
            </header>

            {prize.colors.length > 0 && <ColorRow colors={prize.colors} />}
            {prize.sizes.length > 0 && <SizeRow sizes={prize.sizes} />}

          </div>
        </div>
      </div>
    </motion.section>
  );
}

interface ColorRowProps {
  colors: string[];
}

function ColorRow({ colors }: ColorRowProps) {
  const [selected, setSelected] = useState(0);
  return (
    <div className="flex items-center gap-4">
      <span className="w-16 shrink-0 text-sm font-semibold text-text-primary">Color</span>
      <div className="flex flex-wrap gap-2">
        {colors.map((c, i) => {
          const isActive = i === selected;
          return (
            <button
              key={c + i}
              type="button"
              onClick={() => setSelected(i)}
              aria-label={`Color: ${c}`}
              aria-pressed={isActive}
              className={`relative inline-flex items-center justify-center w-7 h-7 rounded-full transition-[transform,box-shadow] duration-fast ease-out-strong active:scale-[0.92] ${
                isActive ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-transparent' : ''
              }`}
              style={{ backgroundColor: swatchFor(c) }}
            >
              {isActive && <CheckIcon className="w-3.5 h-3.5 text-white drop-shadow" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface SizeRowProps {
  sizes: string[];
}

function SizeRow({ sizes }: SizeRowProps) {
  const [size, setSize] = useState(sizes[0]);
  return (
    <div className="flex items-start gap-4">
      <span className="w-16 shrink-0 text-sm font-semibold text-text-primary pt-2">Size</span>
      <div className="flex flex-col items-start gap-1">
        <select
          value={size}
          onChange={(e) => setSize(e.target.value)}
          className="h-9 px-3 pr-8 rounded-lg border border-border-primary bg-surface-elevated text-sm text-text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500/30"
        >
          {sizes.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}


// ─── Body ───────────────────────────────────────────────────────────────────

interface PrizeBodyProps {
  prize: LearnerPrizeDetail;
}

function PrizeBody({ prize }: PrizeBodyProps) {
  const specRows = useMemo(() => {
    const rows: { label: string; value: string }[] = [];
    if (prize.category) {
      rows.push({
        label: 'Category',
        value: PRIZE_CATEGORY_LABEL[prize.category] ?? prize.category,
      });
    }
    if (prize.sku) rows.push({ label: 'SKU', value: prize.sku });
    if (prize.retail_amount != null) {
      rows.push({
        label: 'Retail value',
        value: `${prize.retail_amount.toLocaleString()} ${prize.retail_currency}`,
      });
    }
    return rows;
  }, [prize]);

  return (
    <section className="px-2 sm:px-4 space-y-6">
      {/* Tab strip — single visual tab to match the design. */}
      <div className="border-b border-border-primary/40">
        <div className="inline-flex items-center h-10 px-4 border-b-2 border-text-primary text-sm font-semibold text-text-primary">
          Description
        </div>
      </div>

      {specRows.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-bold text-text-primary">Specifications</h2>
          <table className="w-full text-sm border-collapse">
            <tbody>
              {specRows.map((row) => (
                <tr key={row.label} className="border-t border-border-primary/40">
                  <th
                    scope="row"
                    className="text-left font-normal text-text-secondary px-3 py-2 align-top w-[40%]"
                  >
                    {row.label}
                  </th>
                  <td className="text-text-primary px-3 py-2 align-top">{row.value}</td>
                </tr>
              ))}
              <tr className="border-t border-border-primary/40" aria-hidden="true" />
            </tbody>
          </table>
        </div>
      )}

      {prize.content && <RichSection title="Product details" html={prize.content} />}

      {prize.tags.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-primary-500">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {prize.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-350 text-xs font-semibold"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

interface RichSectionProps {
  title: string;
  html: string;
}

function RichSection({ title, html }: RichSectionProps) {
  return (
    <div className="space-y-2">
      <h3 className="text-base font-bold text-primary-500">{title}</h3>
      <div
        className="prose prose-sm max-w-none text-text-primary text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// ─── Action bar ─────────────────────────────────────────────────────────────

interface ActionBarProps {
  canClaim: boolean;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ActionBar({ canClaim, isPending, onCancel, onConfirm }: ActionBarProps) {
  return (
    <div className="sticky bottom-0 inset-x-0 z-10 border-t border-border-primary/60 bg-background-primary/90 backdrop-blur-sm">
      <div className="flex items-center justify-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center justify-center min-w-[120px] h-10 px-6 rounded-full border border-border-primary bg-surface-elevated text-sm font-bold text-text-primary hover:bg-background-secondary active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
        >
          Go back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canClaim || isPending}
          className="inline-flex items-center justify-center min-w-[120px] h-10 px-6 rounded-full bg-primary-500 text-sm font-bold hover:bg-primary-600 dark:hover:bg-primary-400 disabled:bg-primary-500/40 disabled:cursor-not-allowed active:scale-[0.97] disabled:active:scale-100 transition-[background-color,transform] duration-fast ease-out-strong"
        >
          {isPending ? 'Claiming…' : 'Claim now'}
        </button>
      </div>
    </div>
  );
}

// ─── Skeleton ──────────────────────────────────────────────────────────────

function ClaimSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="rounded-3xl bg-text-tertiary/10 h-[420px]" />
      <div className="px-2 space-y-3">
        <div className="h-4 w-32 rounded bg-text-tertiary/10" />
        <div className="h-3 w-full rounded bg-text-tertiary/10" />
        <div className="h-3 w-5/6 rounded bg-text-tertiary/10" />
        <div className="h-3 w-2/3 rounded bg-text-tertiary/10" />
      </div>
    </div>
  );
}
